import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  MenuItem,
  Alert,
  AlertTitle,
  Button,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useServices, type Service } from '@/hooks/useServices';
import { useMechanicsWithSkills } from '@/hooks/useStaffUsers';
import { useAllLifts } from '@/hooks/useLifts';
import { DEFAULT_WORK_DURATION_STEP_MINUTES, formatDuration } from '@/utils/duration';
import { guideAnchor, useGuideStore } from '@/lib/guides/engine';
import { ServiceCatalogSelect } from '../ServiceCatalogSelect';
import { isFixedPricePackage } from '../fixedPricePackage';
import { DurationHoursInput } from './DurationHoursInput';
import {
  LaborStepChecklist,
  chosenLaborSteps,
  defaultLaborSelection,
  laborSelectionHours,
  toLaborStepOption,
  type LaborStepOption,
} from './LaborStepChecklist';
import {
  LiftSelect,
  MechanicSelect,
  acceptedLiftTypesForServices,
  requiredSkillsForServices,
} from './ResourceAssignmentFields';

export interface SelectedServiceData {
  rowId: string;
  serviceId: string;
  serviceName: string;
  title?: string;
  estimatedDurationHours: number | null;
  /**
   * True only after staff chose or typed a duration on this row (#2667).
   * Catalog prefills stay false so create does not latch planned_hours_manual.
   * Optional so quote-accept seeding does not have to invent a value.
   */
  durationIsManual?: boolean;
  durationType: 'fixed' | 'variable';
  durationOptions: number[];
  laborItemCount: number;
  /**
   * The service's catalog work steps and the optional ones ticked (#3347).
   * Both optional so quote-accept seeding does not have to carry them; a row
   * without them copies the catalog defaults.
   */
  laborItems?: LaborStepOption[];
  selectedLaborItemIds?: string[];
  extrasCount: number;
  /**
   * The work order's mechanic, repeated on every row (#1984). One work order
   * goes to one mechanic, so the step offers a single selector and writes it
   * to all rows; the per-row shape stays because the wire format is a
   * `serviceMechanics` array parallel to `serviceIds`.
   */
  mechanicId: string;
  /**
   * The work order's lift, repeated on every row — same shape and same reason
   * as `mechanicId` (#1993). Both are exception-case choices: planning
   * normally assigns them, so the step keeps them behind a disclosure.
   */
  liftId: string;
  /**
   * Per-row work comment written at create time (#2119). Lands on
   * `booking_services.notes` — the same field ServiceDetailContent edits.
   * Optional so quote-accept seeding does not have to invent an empty value.
   */
  notes?: string;
}

interface ServiceStepProps {
  selectedServices: SelectedServiceData[];
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedServiceData[]>>;
  setIsDirty: (v: boolean) => void;
  defaultMechanicId?: string;
  /** Workshop step for the custom-duration arrows; 30 when unset (#3091). */
  durationStepMinutes?: number;
}

/**
 * Create-time `serviceDurations` parallel to `serviceIds` (#2667).
 * A non-null entry latches planned_hours_manual; derived catalog rows stay
 * null. Zero/empty custom placeholders are omitted so the payload stays valid.
 * Undefined when every row is derived — the field is then left off create.
 */
export function toCreateServiceDurations(
  selectedServices: readonly SelectedServiceData[],
): (number | null)[] | undefined {
  const durations = selectedServices.map((s) => {
    if (s.durationIsManual !== true) return null;
    const hours = s.estimatedDurationHours;
    if (!(hours != null && hours > 0)) return null;
    return hours;
  });
  return durations.some((d) => d !== null) ? durations : undefined;
}

/** Booking-level hours: omit when services exist so the server derives the sum. */
export function toCreateEstimatedDurationHours(
  serviceIds: readonly string[],
  durationHours: number,
): number | undefined {
  if (serviceIds.length > 0) return undefined;
  return durationHours > 0 ? durationHours : undefined;
}

/**
 * Create-time `serviceLaborItemIds` parallel to `serviceIds` (#3347): the
 * ticked optional steps of each row, null where the service has none.
 * Undefined when no row has optional steps — the field is then left off.
 */
export function toCreateServiceLaborItemIds(
  selectedServices: readonly SelectedServiceData[],
): (string[] | null)[] | undefined {
  const selections = selectedServices.map((s) => {
    const items = s.laborItems ?? [];
    if (!items.some((item) => item.isOptional)) return null;
    return s.selectedLaborItemIds ?? defaultLaborSelection(items);
  });
  return selections.some((selection) => selection !== null) ? selections : undefined;
}

export function ServiceStep({
  selectedServices,
  setSelectedServices,
  setIsDirty,
  defaultMechanicId,
  durationStepMinutes = DEFAULT_WORK_DURATION_STEP_MINUTES,
}: ServiceStepProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');

  const { data: services = [] } = useServices({ isActive: true, sortBy: 'usage' });
  const { data: mechanics = [] } = useMechanicsWithSkills();
  const { data: allLifts = [] } = useAllLifts();

  const [serviceInputValue, setServiceInputValue] = useState('');
  const [customDurationRowIds, setCustomDurationRowIds] = useState<Set<string>>(new Set());
  // Mechanic + lift are the exception, not the rule, so the section starts
  // closed (#1993). A mechanic seeded from the calendar's "create for this
  // mechanic" is an existing choice, so it opens — otherwise the prefill would
  // be invisible.
  const [resourcesOpen, setResourcesOpen] = useState<boolean>(() => Boolean(defaultMechanicId));
  const guideAnchorId = useGuideStore((state) => {
    const active = state.active;
    if (!active) return null;
    return active.guide.steps.find((step) => step.id === active.stepId)?.anchor ?? null;
  });
  useEffect(() => {
    if (guideAnchorId === 'wo-create.mechanic') setResourcesOpen(true);
  }, [guideAnchorId]);

  // Same service can be added multiple times — show full catalog regardless of selection.
  const availableServices = services;

  // Skill union and lift-type intersection are shared with quote acceptance
  // (#2647), so the two flows agree on who qualifies (#1984, #1993).
  const selectedServiceIds = useMemo(
    () => selectedServices.map((sel) => sel.serviceId),
    [selectedServices],
  );
  const requiredSkills = useMemo(
    () => requiredSkillsForServices(selectedServiceIds, services),
    [selectedServiceIds, services],
  );

  const eligibleMechanics = useMemo(
    () => splitByEligibility(mechanics, requiredSkills).eligible,
    [mechanics, requiredSkills],
  );

  const acceptedLiftTypes = useMemo(
    () => acceptedLiftTypesForServices(selectedServiceIds, services),
    [selectedServiceIds, services],
  );

  // All rows carry the same mechanic and lift by construction, so row 0 is the
  // value for both.
  const mechanicId = selectedServices[0]?.mechanicId ?? '';
  const liftId = selectedServices[0]?.liftId ?? '';

  const handleAddService = (service: Service | null) => {
    if (!service) return;

    const isVariable = service.durationType === 'variable';
    const options = (service.durationOptions ?? []).filter((v) => v > 0);
    // Catalog steps start with their defaults ticked (#3347). The server
    // derives the hours from the copied steps whenever the service has any,
    // so the row shows that same sum instead of the catalog duration.
    const laborItems = service.laborItems.map(toLaborStepOption);
    const selectedLaborItemIds = defaultLaborSelection(laborItems);
    setSelectedServices((prev) => [
      ...prev,
      {
        rowId: crypto.randomUUID(),
        serviceId: service.id,
        serviceName: service.name,
        title: '',
        estimatedDurationHours: isVariable
          ? null
          : laborItems.length > 0
            ? laborSelectionHours(laborItems, selectedLaborItemIds)
            : (service.estimatedDurationHours ?? null),
        laborItems,
        selectedLaborItemIds,
        durationIsManual: false,
        durationType: service.durationType ?? 'fixed',
        durationOptions: options,
        laborItemCount: service.laborItems.length,
        extrasCount: service.extras.length,
        // A service added after the mechanic was picked inherits that choice;
        // before any choice it falls back to the calendar's "create for this
        // mechanic" seed. The lift is inherited the same way (#1993).
        mechanicId: selectedServices[0]?.mechanicId ?? defaultMechanicId ?? '',
        liftId: selectedServices[0]?.liftId ?? '',
        notes: '',
      },
    ]);
    setIsDirty(true);
  };

  const handleRemoveService = (rowId: string) => {
    setSelectedServices((prev) => prev.filter((s) => s.rowId !== rowId));
    setCustomDurationRowIds((prev) => {
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    setIsDirty(true);
  };

  // One selection for the work order: it lands on every service (#1984).
  const handleMechanicChange = (newMechanicId: string) => {
    setSelectedServices((prev) => prev.map((s) => ({ ...s, mechanicId: newMechanicId })));
    setIsDirty(true);
  };

  const handleLiftChange = (newLiftId: string) => {
    setSelectedServices((prev) => prev.map((s) => ({ ...s, liftId: newLiftId })));
    setIsDirty(true);
  };

  const handleDurationChange = useCallback((rowId: string, hours: number) => {
    setSelectedServices((prev) =>
      prev.map((s) =>
        s.rowId === rowId ? { ...s, estimatedDurationHours: hours, durationIsManual: true } : s,
      ),
    );
    setIsDirty(true);
  }, [setSelectedServices, setIsDirty]);

  const handleNotesChange = (rowId: string, notes: string) => {
    setSelectedServices((prev) =>
      prev.map((s) => (s.rowId === rowId ? { ...s, notes } : s)),
    );
    setIsDirty(true);
  };

  // Ticking a step changes what gets copied, and with it the derived hours
  // (#3347). A hand-set duration stays as typed.
  const handleLaborSelectionChange = (rowId: string, selectedLaborItemIds: string[]) => {
    setSelectedServices((prev) =>
      prev.map((s) => {
        if (s.rowId !== rowId) return s;
        const derived = s.durationIsManual || s.durationType !== 'fixed'
          ? s.estimatedDurationHours
          : laborSelectionHours(s.laborItems ?? [], selectedLaborItemIds);
        return { ...s, selectedLaborItemIds, estimatedDurationHours: derived };
      }),
    );
    setIsDirty(true);
  };

  const handleDurationSelectChange = useCallback((rowId: string, value: string) => {
    if (value === '__custom__') {
      setCustomDurationRowIds((prev) => new Set(prev).add(rowId));
      handleDurationChange(rowId, 0);
    } else {
      setCustomDurationRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      handleDurationChange(rowId, Number(value));
    }
  }, [handleDurationChange]);

  const totalDuration = selectedServices.reduce(
    (sum, s) => sum + (s.estimatedDurationHours ?? 0),
    0,
  );

  const chosenMechanic = mechanics.find((m) => m.id === mechanicId);
  const chosenMechanicName = chosenMechanic ? `${chosenMechanic.firstName} ${chosenMechanic.lastName}` : '';
  const chosenLiftName = allLifts.find((l) => l.id === liftId)?.name ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Service selector */}
      <Box {...guideAnchor('wo-create.service')}>
      <ServiceCatalogSelect
        options={availableServices}
        value={null}
        onChange={(value) => {
          handleAddService(value);
          setServiceInputValue('');
        }}
        inputValue={serviceInputValue}
        onInputChange={(newValue, reason) => {
          // MUI reports a pick as 'selectOption' with the option label as the new input
          // value. Keep the field empty so the next service can be picked right away
          // instead of first having to blur the field (#1246).
          const clears = reason === 'reset' || reason === 'selectOption';
          setServiceInputValue(clears ? '' : newValue);
        }}
        openOnFocus
        autoFocus
        label={t('create.selectServices')}
        placeholder={t('create.selectServicesPlaceholder')}
      />
      </Box>

      {/* Selected services */}
      {selectedServices.length === 0 ? (
        <Alert severity="info">{t('create.servicesRequired')}</Alert>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('create.selectedServices')}
          </Typography>
          {selectedServices.map((sel) => (
              <Box
                key={sel.rowId}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {/* The catalog name stays the heading; the title is an optional detail under it (#3655). */}
                  <Typography variant="subtitle2" sx={{ width: '100%' }}>
                    {sel.serviceName}
                  </Typography>
                  <TextField
                    label={t('serviceTitle')}
                    helperText={t('serviceTitleHint')}
                    value={sel.title ?? ''}
                    onChange={(e) => {
                      const title = e.target.value;
                      setSelectedServices((prev) => prev.map((s) => s.rowId === sel.rowId ? { ...s, title } : s));
                      setIsDirty(true);
                    }}
                    size="small"
                    inputProps={{ maxLength: 200 }}
                    sx={{ flex: '1 1 240px' }}
                  />
                  {isFixedPricePackage(services.find((service) => service.id === sel.serviceId) ?? {}) && (
                    <Chip size="small" variant="outlined" label={t('detail.fixedPricePackage')} />
                  )}
                  {sel.durationType === 'fixed' && sel.estimatedDurationHours != null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatDuration(sel.estimatedDurationHours)}
                    />
                  )}
                  {sel.laborItemCount > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={sel.laborItems?.some((item) => item.isOptional)
                        ? t('create.laborStepsSelected', {
                            selected: chosenLaborSteps(sel.laborItems, sel.selectedLaborItemIds ?? []).length,
                            total: sel.laborItemCount,
                          })
                        : `${sel.laborItemCount} ${t('create.laborSteps')}`}
                    />
                  )}
                  {sel.extrasCount > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="secondary"
                      label={`${sel.extrasCount} ${t('create.extras')}`}
                    />
                  )}
                </Box>
                <Tooltip title={tc('delete')}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveService(sel.rowId)}
                    color="error"
                    aria-label={tc('delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Optional steps are ticked here (#3347); required ones only show. */}
              {sel.laborItems?.some((item) => item.isOptional) && (
                <LaborStepChecklist
                  items={sel.laborItems}
                  selected={sel.selectedLaborItemIds ?? []}
                  onChange={(next) => handleLaborSelectionChange(sel.rowId, next)}
                />
              )}
              {/* Duration selector for variable-duration services */}
              {sel.durationType === 'variable' && (
                customDurationRowIds.has(sel.rowId) ? (
                  <Box sx={{ mb: 1 }}>
                    <DurationHoursInput
                      value={sel.estimatedDurationHours ?? 0}
                      onChange={(hours) => handleDurationChange(sel.rowId, hours)}
                      stepMinutes={durationStepMinutes}
                      label={t('create.customDurationLabel')}
                    />
                  </Box>
                ) : sel.durationOptions.length > 0 ? (
                  <TextField
                    select
                    label={t('create.selectDuration')}
                    value={sel.estimatedDurationHours ?? ''}
                    onChange={(e) => handleDurationSelectChange(sel.rowId, e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {sel.durationOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt} h
                      </MenuItem>
                    ))}
                    <MenuItem value="__custom__">
                      {t('create.customDuration')}
                    </MenuItem>
                  </TextField>
                ) : (
                  <Box sx={{ mb: 1 }}>
                    <DurationHoursInput
                      value={sel.estimatedDurationHours ?? 0}
                      onChange={(hours) => handleDurationChange(sel.rowId, hours)}
                      stepMinutes={durationStepMinutes}
                      label={t('create.customDurationLabel')}
                    />
                  </Box>
                )
              )}
              <TextField
                label={t('create.serviceNotes')}
                placeholder={t('create.serviceNotesPlaceholder')}
                value={sel.notes ?? ''}
                onChange={(e) => handleNotesChange(sel.rowId, e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1 }}
                inputProps={{ maxLength: 2000 }}
              />
            </Box>
          ))}

          {/* Mechanic and lift are exception-case choices: planning normally
              assigns both, so they sit behind a disclosure instead of in the
              main flow (#1993). One of each covers the whole work order. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => { setResourcesOpen((open) => !open); }}
                aria-expanded={resourcesOpen}
                aria-controls={RESOURCES_SECTION_ID}
                startIcon={
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: resourcesOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 150ms',
                    }}
                  />
                }
                sx={{ textTransform: 'none' }}
              >
                {t('create.mechanicAndLift')}
              </Button>
              {/* A choice must stay visible with the section closed, or the
                  user cannot tell it was made. */}
              {!resourcesOpen && (chosenMechanicName || chosenLiftName) && (
                <Typography variant="caption" color="text.secondary">
                  {[chosenMechanicName, chosenLiftName].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>

            {/* unmountOnExit: closed means the selectors are not part of the
                form at all, rather than present-but-hidden. */}
            <Collapse in={resourcesOpen} id={RESOURCES_SECTION_ID} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
                <Alert severity="info" variant="outlined">
                  <AlertTitle sx={{ mb: 0 }}>{t('create.mechanicAndLiftHintTitle')}</AlertTitle>
                  {t('create.mechanicAndLiftHint')}
                </Alert>
                <MechanicSelect
                  mechanicId={mechanicId}
                  eligibleMechanics={eligibleMechanics}
                  allMechanics={mechanics}
                  requiredSkills={requiredSkills}
                  onChange={handleMechanicChange}
                />
                <LiftSelect
                  liftId={liftId}
                  allLifts={allLifts}
                  acceptedLiftTypes={acceptedLiftTypes}
                  onChange={handleLiftChange}
                />
              </Box>
            </Collapse>
          </Box>
        </Stack>
      )}

      {/* Total duration */}
      {totalDuration > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('create.totalDuration')}: {totalDuration} {t('create.hoursSuffix')}
        </Typography>
      )}
    </Box>
  );
}


import { splitByEligibility } from '@/utils/skill-matching';

/** Ties the disclosure button to the region it controls for assistive tech. */
const RESOURCES_SECTION_ID = 'wo-create-mechanic-lift';
